import {
  Box,
  Collapse,
  Flex,
  IconButton,
  Text,
  useColorModeValue
} from "@chakra-ui/react";
import { Feature } from "geojson";
import L from "leaflet";
import { useEffect, useRef, useState } from "react";
import { IoChevronDownCircleOutline } from "react-icons/io5";
import { TextGray } from "components/Atomos/Texts/TextGray";
import { useTypedSelector } from "store/hooks";
import { PropriedadesFeicao } from "store/modules/consultaprevia/types";
import { chaveDaFeicao, OrigemCamada } from "../../contexts/CamadasProvider";
import { GrupoCamadas, ItemCamada } from "./GrupoCamadas";

const paraItens = (
  features: Feature[] = [],
  origem: OrigemCamada
): ItemCamada[] => {
  const porChave = new Map<string, ItemCamada>();

  features.forEach((feature) => {
    const props = (feature.properties ?? {}) as Partial<PropriedadesFeicao>;
    const chave = chaveDaFeicao(feature, origem);

    if (!props.sigla || porChave.has(chave)) return;

    porChave.set(chave, {
      chave,
      sigla: props.sigla,
      nome: props.nome ?? "",
      corPreenchimento: props.corPreenchimento ?? "#63B3ED",
      corBorda: props.corBorda ?? "#3182CE"
    });
  });

  return Array.from(porChave.values()).sort((a, b) =>
    a.sigla.localeCompare(b.sigla, "pt-BR")
  );
};

/**
 * Controle de camadas, logo abaixo da busca. Liga e desliga cada zona e cada
 * área especial, com a cor com que aparecem no mapa — acumula a função de
 * legenda, porque uma legenda à parte repetiria a mesma lista sem deixar mexer
 * em nada.
 */
export const ControleCamadas = () => {
  const [expandido, setExpandido] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);
  const bgColor = useColorModeValue("white", "gray.900");

  const zonas = useTypedSelector(
    (state) => state.consultaprevia.obterZonasGeo.successPayload
  );
  const areas = useTypedSelector(
    (state) => state.consultaprevia.obterAreasEspeciaisGeo.successPayload
  );

  // A lista rola por dentro: sem isto, a roda do mouse sobre ela daria zoom no
  // mapa em vez de percorrer as camadas.
  useEffect(() => {
    if (containerRef.current) {
      L.DomEvent.disableScrollPropagation(containerRef.current);
      L.DomEvent.disableClickPropagation(containerRef.current);
    }
  }, []);

  const itensZonas = paraItens(zonas?.features, "zona");
  const itensAreas = paraItens(areas?.features, "area");
  const total = itensZonas.length + itensAreas.length;

  if (total === 0) return null;

  return (
    <Box className="leaflet-control" ml="0px !important" pl="10px" zIndex={999}>
      <Box
        ref={containerRef}
        bgColor={bgColor}
        borderRadius="md"
        boxShadow="md"
        w="min(100vw - 20px, 360px)"
        px="3"
        py="2"
      >
        <Flex
          alignItems="center"
          justifyContent="space-between"
          cursor="pointer"
          onClick={() => setExpandido((atual) => !atual)}
        >
          <Box>
            <Text fontSize="sm" fontWeight="bold">
              Camadas
            </Text>
            <TextGray fontSize="xs">{`${total} no plano diretor`}</TextGray>
          </Box>
          <IconButton
            size="sm"
            variant="ghost"
            colorScheme="main"
            aria-label={expandido ? "Recolher camadas" : "Expandir camadas"}
            icon={
              <Box
                transform={expandido ? "rotate(180deg)" : "rotate(0deg)"}
                transition="transform 0.2s ease-in-out"
              >
                <IoChevronDownCircleOutline size={22} />
              </Box>
            }
            onClick={(e) => {
              e.stopPropagation();
              setExpandido((atual) => !atual);
            }}
          />
        </Flex>

        <Collapse in={expandido} animateOpacity>
          {/* A margem negativa cancela o padding: a área de rolagem chega
              perto da borda do cartão e o conteúdo continua alinhado com o
              cabeçalho, mas o anel de foco dos checkboxes passa a ter folga
              para não ser cortado pelo overflow. */}
          <Box maxH="18rem" overflowY="auto" px="1" pb="3">
            <GrupoCamadas titulo="Zonas urbanas" itens={itensZonas} />
            <GrupoCamadas titulo="Áreas especiais" itens={itensAreas} />
          </Box>
        </Collapse>
      </Box>
    </Box>
  );
};
